import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TeamTag } from "./TeamTag";
import { teamColor } from "../utils/teamColor";

/**
 * Inline colored kelompok badges for a user, collapsing overflow into a
 * popover instead of letting chips stack/wrap (hasil meet.txt bug#4: "takutnya
 * tumpuk2 kasih drop down aja"). Positioning logic is adapted from
 * CustomSelect.jsx (portal + scroll/resize reposition + outside-click close)
 * rather than reusing that component directly — CustomSelect is a
 * single-value picker, this is a read-only badge list, and CustomSelect is
 * already relied on in 10+ places elsewhere.
 */
export default function TeamBadgeList({ teams = [], max = 3 }) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const shown = teams.slice(0, max);
  const overflow = teams.slice(max);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const estHeight = Math.min(overflow.length * 30 + 16, 200);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < estHeight + 8 && rect.top > estHeight;
    setStyle({
      position: "fixed",
      left: rect.left,
      zIndex: 9999,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    const onMouseDown = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("mousedown", onMouseDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!teams.length) {
    return <TeamTag name={null} />;
  }

  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {shown.map((t) => (
        <TeamTag key={t.id} name={t.name} color={teamColor(t.id)} />
      ))}
      {overflow.length > 0 && (
        <>
          <button
            type="button"
            ref={triggerRef}
            onClick={() => setOpen((v) => !v)}
            className="team-tag"
            style={{ cursor: "pointer", border: "1px dashed var(--border)", background: "none" }}
          >
            +{overflow.length}
          </button>
          {open && createPortal(
            <div
              ref={popoverRef}
              className="glass-dropdown rounded-xl p-2 flex flex-col gap-1"
              style={style}
            >
              {overflow.map((t) => (
                <TeamTag key={t.id} name={t.name} color={teamColor(t.id)} />
              ))}
            </div>,
            document.body
          )}
        </>
      )}
    </span>
  );
}
